// import { Button, Icon, Tooltip } from '@pypestream/design-system';
// import { observer } from 'mobx-react-lite';

// import { useLoadingState } from '../../../hooks';
// import { tableStore } from '../../../stores/table-store';

// export const PublishButton = observer(
//   ({ onClick }: { onClick: () => void }) => {
//     const isCurrentRouteLoading = useLoadingState();
//     const { validationMessage } = tableStore;
//     const isValidDraft =
//       validationMessage === 'Version is valid and can be deployed.';

//     const isDraftLoading = !validationMessage || isCurrentRouteLoading;
//     const isPrimaryButton = isDraftLoading || isValidDraft;

//     const button = (
//       <Button
//         size="small"
//         disabled={!isValidDraft}
//         onClick={onClick}
//         variant={isPrimaryButton ? 'primary' : 'warning'}
//       >
//         <Icon name={isPrimaryButton ? 'deploy' : 'info'} slot="prefix" />
//         Publish
//       </Button>
//     );

//     return isPrimaryButton ? (
//       button
//     ) : (
//       <Tooltip>
//         {button}
//         <p slot="content" style={{ whiteSpace: 'pre-line', fontSize: '12px' }}>
//           {validationMessage}
//         </p>
//       </Tooltip>
//     );
//   }
// );

import { Button, Icon, Tooltip } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { observer } from 'mobx-react-lite';

import { useLoadingState } from '../../../hooks';
import { tableStore } from '../../../stores/table-store';
import { findNodeNumberProblem } from '../../../stores/utils';
import { isDraftInvalidForPublish } from './publish-button.utils';

export const PublishButton = observer(
  ({
    onClick,
    disabled,
    tooltipContent,
    validating,
    pending,
  }: {
    onClick: () => void;
    disabled?: boolean;
    tooltipContent?: string;
    /**
     * PSTUDIO-4242: a validation pass is running. The compiler errors are then
     * the PREVIOUS pass's verdict, and this pass may clear them, so they must
     * not disable the button — the click is queued and judged once the pass
     * ends. A node-number problem still disables it (see the helper).
     */
    validating?: boolean;
    /** A click is queued behind the running pass. */
    pending?: boolean;
  }) => {
    const { t } = useTranslation();
    const isCurrentRouteLoading = useLoadingState();
    // PSTUDIO-4000: effective list — a duplicate node number makes the draft
    // invalid immediately, not only once a recompile has reported it.
    const isInvalidDraft = isDraftInvalidForPublish({
      validating: !!validating,
      effectiveErrorCount: tableStore.effectiveErrors.length,
      hasNodeNumberProblem:
        !!validating &&
        findNodeNumberProblem(tableStore.rowsForValidation) !== null,
    });
    const { isWaitingForBackendData, isWaitingForLiveCorpusIdOnly } =
      tableStore;
    const isWaitingForBackend =
      isWaitingForBackendData || isWaitingForLiveCorpusIdOnly;
    const isDisabled =
      isInvalidDraft ||
      isCurrentRouteLoading ||
      isWaitingForBackend ||
      disabled;
    // Localised like the three branches in `primary-nav.tsx` that this one
    // OVERRIDES (PSTUDIO-4120). Left as a literal it made one button read from
    // the catalog in three states and from English source in the fourth — the
    // state a first-time user is most likely to hit.
    const effectiveTooltip = isWaitingForBackend
      ? t('pro-studio/navigation:publish_button.tooltip.waiting_for_backend', {
          defaultValue:
            "We're still setting things up. Publishing will be available soon.",
        })
      : tooltipContent;
    const button = (
      <Button
        test-id="ps-publish-version-button"
        size="small"
        disabled={isDisabled}
        loading={pending}
        onClick={onClick}
        variant="primary"
      >
        <Icon name="deploy" slot="prefix" />
        Publish
      </Button>
    );

    return effectiveTooltip ? (
      <Tooltip content={effectiveTooltip}>{button}</Tooltip>
    ) : (
      button
    );
  }
);
